import { animated, useSpring } from '@react-spring/web';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayS, LabelL } from '@sorare/core/src/atoms/typography';
import { Box } from '@sorare/core/src/atoms/ui/Box';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  smallMobileHeightAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import footballBg from './assets/footballBg.webp';
import mlbBg from './assets/mlbBg.webp';
import nbaBg from './assets/nbaBg.webp';
import rewards from './assets/rewards.webp';

const mainMessage = defineMessages({
  title: {
    defaultMessage: 'Unlock Perks',
  },
  subtitle: {
    defaultMessage: 'Seal more cards to unlock perks like Boosted rewards!',
  },
});

const mainImageBySport = {
  [Sport.BASEBALL]: mlbBg,
  [Sport.NBA]: nbaBg,
  [Sport.FOOTBALL]: footballBg,
};

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
  isolation: isolate;
  justify-content: space-between;

  @media ${tabletAndAbove} {
    gap: var(--double-unit);
  }

  & > * {
    z-index: 1;
    position: relative;
  }
`;

const Content = styled(Vertical)`
  width: 100%;
  flex: 1;
`;

const Padded = styled(Horizontal)`
  padding: 0 var(--double-unit);
  flex: 1;
  display: flex;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: auto;
  padding: var(--double-unit);
  display: flex;
  gap: var(--unit);
`;

const StyledButton = styled(Button)`
  flex: 1;
`;

const TextContent = styled(Vertical)`
  height: 80px;
  display: flex;
  align-items: center;
  position: relative;

  @media ${smallMobileHeightAndAbove} {
    margin-top: var(--triple-unit);
  }

  & > * {
    padding-inline: var(--double-unit);
  }
`;

const Img = styled(animated.img)`
  margin: 0 auto;
  pointer-events: none;
  width: 100%;
  user-select: none;
  object-fit: cover;
  max-width: 90%;
`;

const SpaceAroundVertical = styled(Vertical)`
  flex: 1;
  justify-content: space-around;
`;

type Props = {
  onClose: () => void;
  prevStep: () => void;
};

export const UnlockPerks = ({ onClose, prevStep }: Props) => {
  const { sport } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const [direction, setDirection] = useState<'next' | 'back'>('next');

  const transitions = useSpring({
    opacity: 1,
    transform: 'translateX(0%)',
    from: {
      opacity: 0,
      transform:
        direction === 'next' ? 'translateX(100%)' : 'translateX(-100%)',
    },
    config: { tension: 200, friction: 20 },
  });

  const imageSpring = useSpring({
    opacity: 1,
    from: { opacity: 0 },
    config: { tension: 200, friction: 20 },
  });
  if (!currentUser) return null;

  return (
    <Wrapper>
      <Content>
        <TextContent gap={0.5}>
          <animated.div
            style={{ ...transitions, position: 'absolute', width: '100%' }}
          >
            <DisplayS className="text-center">
              <FormattedMessage
                {...mainMessage.title}
                values={{ nickname: currentUser?.nickname }}
              />
            </DisplayS>
          </animated.div>
        </TextContent>
        <SpaceAroundVertical
          center
          style={{
            background: `url(${mainImageBySport[sport]}) no-repeat center/140%`,
          }}
        >
          <Img style={imageSpring} src={rewards} alt="" className="boxImage" />
        </SpaceAroundVertical>
        <div>
          <Padded center>
            <Box>
              <LabelL color="var(--c-nd-600)" className="text-center">
                <FormattedMessage {...mainMessage.subtitle} />
              </LabelL>
            </Box>
          </Padded>
        </div>
      </Content>
      <ButtonWrapper>
        <StyledButton
          fullWidth
          size="large"
          color="quaternary"
          disableDebounce
          onClick={() => {
            setDirection('back');
            prevStep();
          }}
        >
          <FormattedMessage {...glossary.back} />
        </StyledButton>
        <StyledButton
          fullWidth
          size="large"
          color="secondary"
          disableDebounce
          onClick={() => {
            setDirection('next');
            onClose();
          }}
        >
          <FormattedMessage {...glossary.gotIt} />
        </StyledButton>
      </ButtonWrapper>
    </Wrapper>
  );
};
